import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  useTheme,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Autocomplete,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Checkbox,
  Button,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import { SelectChangeEvent } from '@mui/material/Select';
import { ResponsiveLine } from '@nivo/line';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsivePie } from '@nivo/pie';
import Header from '../../components/Header';
import OrderDetailsModal from '../../components/OrderDetailsModal';
import { FilterContext } from '../../contexts/FilterContext';
import TableWidget from '../../helpers/tableBuilder';
import { formatEur, formatPct, TERMS } from '../../helpers/kpiDefinitions';
import {
  fetchPlxOrderStats,
  fetchPlxFilterOptions,
  PlxOrderStats,
  PlxFilterOptions,
  fetchPlxTimeline,
  fetchPlxProductPerformance,
  fetchPlxSourcePerformance,
  fetchPlxWkzPerformance,
  fetchPlxBlogPerformance,
  fetchPlxSubscriptionDistribution,
  fetchPlxRetentionCurve,
  fetchPlxMonthlyRevenue,
  PlxTimelineData,
  PlxProductPerformance,
  PlxSourcePerformance,
  PlxWkzPerformance,
  PlxBlogPerformance,
  PlxSubscriptionDistribution,
  PlxRetentionCurve,
  PlxMonthlyRevenue,
} from '../../helpers/plxOrdersData';
import { determineAggregation } from '../../helpers/chartUtils';
import { tokens } from '../../theme';
import LoadingSpinner from '../../components/LoadingSpinner';
import { formatNumber } from '../../helpers/numberUtils';
import OrderList, { ORDER_LIST_ANCHOR } from './OrderList';

/**
 * Zahlen aus der API kommen als Zeichenketten (DECIMAL ueber mysql2). Eine Stelle statt vier —
 * dieselbe Normalisierung gilt fuer die Zeilen UND die Summenzeile aus dem Backend.
 */
interface PerformanceZahlenfelder {
  total_orders: number;
  anteil: number;
  paid_orders: number;
  total_revenue: number;
  expected_revenue: number;
  expected_anfo_value: number;
  conversion_rate: number;
  churn_rate: number;
  retention_rate: number;
  abo_2_60_days: number;
  avg_lifetime_value: number;
  avg_current_lifetime_days: number;
}

const normalisierePerformanceZeile = <T extends PerformanceZahlenfelder>(item: T): T => ({
  ...item,
  total_orders: Number(item.total_orders),
  anteil: Number(item.anteil),
  paid_orders: Number(item.paid_orders),
  total_revenue: Number(item.total_revenue),
  expected_revenue: Number(item.expected_revenue) || 0,
  expected_anfo_value: Number(item.expected_anfo_value) || 0,
  conversion_rate: Number(item.conversion_rate),
  churn_rate: Number(item.churn_rate),
  retention_rate: Number(item.retention_rate),
  abo_2_60_days: Number(item.abo_2_60_days),
  avg_lifetime_value: Number(item.avg_lifetime_value) || 0,
  avg_current_lifetime_days: Number(item.avg_current_lifetime_days) || 0,
});

const PlxOrdersPage: FC = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState, setFilterState } = useContext(FilterContext)!;
  const [stats, setStats] = useState<PlxOrderStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState<PlxFilterOptions>({
    werbeaktionen: [],
    artikel: [],
    banner: [],
    sources: [],
  });

  // Visualization data state
  const [timelineData, setTimelineData] = useState<PlxTimelineData[]>([]);
  const [productPerformanceData, setProductPerformanceData] = useState<PlxProductPerformance[]>([]);
  const [sourcePerformanceData, setSourcePerformanceData] = useState<PlxSourcePerformance[]>([]);
  const [wkzPerformanceData, setWkzPerformanceData] = useState<PlxWkzPerformance[]>([]);
  const [blogPerformanceData, setBlogPerformanceData] = useState<PlxBlogPerformance[]>([]);
  // Summenzeilen kommen seit 2026-09-22 aus dem Backend — jede Quote dort hat ihren eigenen
  // Nenner, den das Frontend aus den Zeilenwerten nicht rekonstruieren kann.
  const [productPerformanceSummary, setProductPerformanceSummary] =
    useState<PlxProductPerformance | null>(null);
  const [sourcePerformanceSummary, setSourcePerformanceSummary] =
    useState<PlxSourcePerformance | null>(null);
  const [wkzPerformanceSummary, setWkzPerformanceSummary] = useState<PlxWkzPerformance | null>(
    null,
  );
  const [blogPerformanceSummary, setBlogPerformanceSummary] = useState<PlxBlogPerformance | null>(
    null,
  );
  // TableWidget lädt seine Zeilen nur bei Filterwechsel, und `getData` liefert in dem Moment noch die
  // Daten der vorigen Auswahl; die Summenzeile dagegen ist immer aktuell. Bis 27.10.2026 standen deshalb
  // Zeilen einer alten Auswahl über der Gesamtzeile der neuen (Blog 77 + 3 ≠ Gesamt 103). Der Zähler wird
  // je geladenem Datensatz hochgezählt und als `key` gesetzt — die Tabellen bauen mit den frischen Daten neu auf.
  const [vizStand, setVizStand] = useState(0);
  const [subscriptionDistribution, setSubscriptionDistribution] = useState<
    PlxSubscriptionDistribution[]
  >([]);
  const [retentionCurveData, setRetentionCurveData] = useState<PlxRetentionCurve[]>([]);
  const [monthlyRevenueData, setMonthlyRevenueData] = useState<PlxMonthlyRevenue[]>([]);
  const [aggregation, setAggregation] = useState<string>('daily');
  const [vizLoading, setVizLoading] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string | null>(null);
  const [orderCount, setOrderCount] = useState<number | null>(null);

  // Timezone-safe date formatter for URL
  const formatDateForURL = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Read URL parameters on mount to initialize filters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const updates: any = {};

    // Read startDate from URL, or set default to 3 months ago
    const startDateParam = urlParams.get('startDate');
    if (startDateParam) {
      const parsedDate = moment(startDateParam, 'YYYY-MM-DD', true);
      if (parsedDate.isValid()) {
        updates.startDate = parsedDate.toDate();
      }
    } else {
      updates.startDate = moment().subtract(3, 'months').toDate();
    }

    // Read endDate from URL if present
    const endDateParam = urlParams.get('endDate');
    if (endDateParam) {
      const parsedDate = moment(endDateParam, 'YYYY-MM-DD', true);
      if (parsedDate.isValid()) {
        updates.endDate = parsedDate.toDate();
      }
    }

    // Read array parameters (comma-separated)
    const artikel = urlParams.get('artikel');
    if (artikel) {
      updates.artikel = artikel.split(',').filter((v) => v.trim());
    }

    const vertrag_sts = urlParams.get('vertrag_sts');
    if (vertrag_sts) {
      updates.vertrag_sts = vertrag_sts.split(',').filter((v) => v.trim());
    }

    const banner = urlParams.get('banner');
    if (banner) {
      updates.banner = banner.split(',').filter((v) => v.trim());
    }

    const werbeaktion_wbkz = urlParams.get('werbeaktion_wbkz');
    if (werbeaktion_wbkz) {
      updates.werbeaktion_wbkz = werbeaktion_wbkz.split(',').filter((v) => v.trim());
    }

    const subscription_type = urlParams.get('subscription_type');
    if (subscription_type) {
      updates.subscription_type = subscription_type.split(',').filter((v) => v.trim());
    }

    const source = urlParams.get('source');
    if (source) {
      updates.source = source.split(',').filter((v) => v.trim());
    }

    const blog = urlParams.get('blog');
    if (blog) {
      updates.blog = blog.split(',').filter((v) => v.trim());
    }

    // Read string parameters
    const banner_contains = urlParams.get('banner_contains');
    if (banner_contains) {
      updates.banner_contains = banner_contains;
    }

    const banner_not_contains = urlParams.get('banner_not_contains');
    if (banner_not_contains) {
      updates.banner_not_contains = banner_not_contains;
    }

    // Read boolean parameter: excludeTestOrders (default: true)
    const excludeTestOrders = urlParams.get('excludeTestOrders');
    if (excludeTestOrders !== null) {
      updates.excludeTestOrders = excludeTestOrders !== 'false';
    } else {
      // Set default to true (exclude test orders by default)
      updates.excludeTestOrders = true;
    }

    // Update filter state with dates and custom filters
    if (Object.keys(updates).length > 0) {
      setFilterState((prev) => ({
        ...prev,
        ...(updates.startDate && { startDate: updates.startDate }),
        ...(updates.endDate && { endDate: updates.endDate }),
        custom: {
          ...prev.custom,
          ...Object.keys(updates).reduce((acc, key) => {
            if (key !== 'startDate' && key !== 'endDate') {
              acc[key] = updates[key];
            }
            return acc;
          }, {} as any),
        },
      }));
    }
  }, []); // Run only once on mount

  // Update URL when filters change
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Update date parameters using timezone-safe formatting
    if (filterState.startDate) {
      urlParams.set('startDate', formatDateForURL(filterState.startDate));
    } else {
      urlParams.delete('startDate');
    }

    if (filterState.endDate) {
      urlParams.set('endDate', formatDateForURL(filterState.endDate));
    } else {
      urlParams.delete('endDate');
    }

    // Update array parameters
    if (filterState.custom?.artikel && filterState.custom.artikel.length > 0) {
      urlParams.set('artikel', filterState.custom.artikel.join(','));
    } else {
      urlParams.delete('artikel');
    }

    if (filterState.custom?.vertrag_sts && filterState.custom.vertrag_sts.length > 0) {
      urlParams.set('vertrag_sts', filterState.custom.vertrag_sts.join(','));
    } else {
      urlParams.delete('vertrag_sts');
    }

    if (filterState.custom?.banner && filterState.custom.banner.length > 0) {
      urlParams.set('banner', filterState.custom.banner.join(','));
    } else {
      urlParams.delete('banner');
    }

    if (filterState.custom?.werbeaktion_wbkz && filterState.custom.werbeaktion_wbkz.length > 0) {
      urlParams.set('werbeaktion_wbkz', filterState.custom.werbeaktion_wbkz.join(','));
    } else {
      urlParams.delete('werbeaktion_wbkz');
    }

    if (filterState.custom?.subscription_type && filterState.custom.subscription_type.length > 0) {
      urlParams.set('subscription_type', filterState.custom.subscription_type.join(','));
    } else {
      urlParams.delete('subscription_type');
    }

    if (filterState.custom?.source && filterState.custom.source.length > 0) {
      urlParams.set('source', filterState.custom.source.join(','));
    } else {
      urlParams.delete('source');
    }

    if (filterState.custom?.blog && filterState.custom.blog.length > 0) {
      urlParams.set('blog', filterState.custom.blog.join(','));
    } else {
      urlParams.delete('blog');
    }

    // Update string parameters
    if (filterState.custom?.banner_contains) {
      urlParams.set('banner_contains', filterState.custom.banner_contains);
    } else {
      urlParams.delete('banner_contains');
    }

    if (filterState.custom?.banner_not_contains) {
      urlParams.set('banner_not_contains', filterState.custom.banner_not_contains);
    } else {
      urlParams.delete('banner_not_contains');
    }

    // Update boolean parameter: excludeTestOrders (only set if false, default is true)
    if (filterState.custom?.excludeTestOrders === false) {
      urlParams.set('excludeTestOrders', 'false');
    } else {
      urlParams.delete('excludeTestOrders'); // Default is true
    }

    // Update URL without adding to browser history
    const newUrl = urlParams.toString()
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  }, [
    filterState.startDate,
    filterState.endDate,
    filterState.custom?.artikel,
    filterState.custom?.vertrag_sts,
    filterState.custom?.banner,
    filterState.custom?.werbeaktion_wbkz,
    filterState.custom?.subscription_type,
    filterState.custom?.source,
    filterState.custom?.blog,
    filterState.custom?.banner_contains,
    filterState.custom?.banner_not_contains,
    filterState.custom?.excludeTestOrders,
  ]);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await fetchPlxOrderStats(filterState);
        setStats(data);
      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoading(false);
      }
    };
    loadStats();
  }, [filterState]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const options = await fetchPlxFilterOptions();
        setFilterOptions(options);
      } catch (error) {
        console.error('Error loading filter options:', error);
      }
    };
    loadFilterOptions();
  }, []);

  // Seit 2026-10-06 sind die Quellen die Kanal-Labels aus helpers/orderChannel.ts. Ein gespeicherter
  // Link mit einem alten Wert („SAM", „Newsletter", „Unbekannt" …) fände nichts mehr und zeigte still
  // eine leere Seite. Kein Mapping alt→neu: die alten Eimer decken sich nicht mit den neuen.
  const [entfernteQuellen, setEntfernteQuellen] = useState<string[]>([]);
  const gewaehlteQuellen = filterState.custom?.source as string[] | undefined;
  useEffect(() => {
    const bekannt = filterOptions.sources ?? [];
    if (bekannt.length === 0 || !gewaehlteQuellen?.length) return;
    const unbekannt = gewaehlteQuellen.filter((s) => !bekannt.includes(s));
    if (unbekannt.length === 0) return;
    setEntfernteQuellen(unbekannt);
    setFilterState((prev) => {
      const behalten = ((prev.custom?.source as string[] | undefined) ?? []).filter((s) =>
        bekannt.includes(s),
      );
      return { ...prev, custom: { ...prev.custom, source: behalten.length > 0 ? behalten : undefined } };
    });
  }, [filterOptions.sources, gewaehlteQuellen]);

  // Auto-determine aggregation based on date range
  useEffect(() => {
    if (filterState.startDate && filterState.endDate) {
      const autoAggregation = determineAggregation(filterState.startDate, filterState.endDate);
      setAggregation(autoAggregation);
    }
  }, [filterState.startDate, filterState.endDate]);

  // Load visualization data
  useEffect(() => {
    // Eine überholte Antwort (Datum schnell zweimal geändert) darf die neuere nicht überschreiben.
    let veraltet = false;
    const loadVisualizationData = async () => {
      setVizLoading(true);
      try {
        const [timeline, productPerf, sourcePerf, wkzPerf, blogPerf, subDist, retention, revenue] =
          await Promise.all([
            fetchPlxTimeline(filterState, aggregation),
            fetchPlxProductPerformance(filterState),
            fetchPlxSourcePerformance(filterState),
            fetchPlxWkzPerformance(filterState),
            fetchPlxBlogPerformance(filterState),
            fetchPlxSubscriptionDistribution(filterState),
            fetchPlxRetentionCurve(filterState),
            fetchPlxMonthlyRevenue(filterState),
          ]);
        if (veraltet) return;

        setTimelineData(timeline);
        setProductPerformanceData(productPerf.data.map(normalisierePerformanceZeile));
        setProductPerformanceSummary(
          productPerf.summary ? normalisierePerformanceZeile(productPerf.summary) : null,
        );
        setSourcePerformanceData(sourcePerf.data.map(normalisierePerformanceZeile));
        setSourcePerformanceSummary(
          sourcePerf.summary ? normalisierePerformanceZeile(sourcePerf.summary) : null,
        );
        setWkzPerformanceData(wkzPerf.data.map(normalisierePerformanceZeile));
        setWkzPerformanceSummary(
          wkzPerf.summary ? normalisierePerformanceZeile(wkzPerf.summary) : null,
        );
        setBlogPerformanceData(blogPerf.data.map(normalisierePerformanceZeile));
        setBlogPerformanceSummary(
          blogPerf.summary ? normalisierePerformanceZeile(blogPerf.summary) : null,
        );
        setSubscriptionDistribution(subDist);
        setRetentionCurveData(retention);
        setMonthlyRevenueData(revenue);
        setVizStand((n) => n + 1);
      } catch (error) {
        console.error('Error loading visualization data:', error);
      } finally {
        if (!veraltet) setVizLoading(false);
      }
    };
    loadVisualizationData();
    return () => {
      veraltet = true;
    };
  }, [filterState, aggregation]);

  const handleOpenOrder = useCallback((ordernumber: string) => {
    setSelectedOrderNumber(ordernumber);
    setModalOpen(true);
  }, []);

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedOrderNumber(null);
  };

  return (
    <Box m="20px">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Header title="Anfo-Übersicht" subtitle="PLX Bestellungen und Abonnements" />
        {/* Die Liste steht unter Kacheln und Diagrammen — der Sprung erspart rund 7.000 px Scrollen. */}
        <Button
          variant="outlined"
          endIcon={<ArrowDownwardIcon />}
          onClick={() =>
            document.getElementById(ORDER_LIST_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
          sx={{ mt: 1, color: 'text.primary', borderColor: 'divider' }}
        >
          Zur Bestellliste{orderCount !== null ? ` (${formatNumber(orderCount, 0)})` : ''}
        </Button>
      </Box>

      {entfernteQuellen.length > 0 && (
        <Alert severity="info" onClose={() => setEntfernteQuellen([])} sx={{ mb: 2 }}>
          {entfernteQuellen.length === 1 ? 'Die Quelle ' : 'Die Quellen '}
          {entfernteQuellen
            .map((q) => `„${q}“`)
            .join(', ')
            .replace(/, ([^,]*)$/, ' und $1')}{' '}
          gibt es nicht mehr (Kanal-Zuordnung umbenannt) — Filter entfernt. Die aktuellen Quellen stehen in
          der Auswahl „Quelle“.
        </Alert>
      )}

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          mt={2}
          mb={3}
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            flexWrap: 'wrap',
            alignItems: 'center',
          }}
        >
          <DatePicker
            label="Start-Datum"
            value={filterState.startDate ? dayjs(filterState.startDate) : null}
            onChange={(date: Dayjs | null) => {
              setFilterState((prev) => ({ ...prev, startDate: date?.toDate() || null }));
            }}
            format={'DD.MM.YYYY'}
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 150 },
              },
            }}
          />
          <DatePicker
            label="End-Datum"
            value={filterState.endDate ? dayjs(filterState.endDate) : null}
            onChange={(date: Dayjs | null) => {
              setFilterState((prev) => ({ ...prev, endDate: date?.toDate() || null }));
            }}
            format={'DD.MM.YYYY'}
            slotProps={{
              textField: {
                size: 'small',
                sx: { minWidth: 150 },
              },
            }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filterState.custom?.excludeTestOrders !== false}
                onChange={(e) => {
                  setFilterState((prev) => ({
                    ...prev,
                    custom: {
                      ...prev.custom,
                      excludeTestOrders: e.target.checked,
                    },
                  }));
                }}
                size="small"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body2">Test-Orders ausblenden</Typography>
                <Tooltip title="Test-Orders aus Firmenzentrale (PLZ 53119) nicht anzeigen" arrow>
                  <InfoOutlinedIcon fontSize="small" sx={{ color: colors.grey[300] }} />
                </Tooltip>
              </Box>
            }
            sx={{ ml: 1 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Produkt</InputLabel>
            <Select<string[]>
              multiple
              value={filterState.custom?.artikel || []}
              onChange={(event: SelectChangeEvent<string[]>) => {
                const value = event.target.value;
                const artikelArray = typeof value === 'string' ? value.split(',') : value;
                setFilterState((prev) => ({
                  ...prev,
                  custom: {
                    ...prev.custom,
                    artikel: artikelArray.length > 0 ? artikelArray : undefined,
                  },
                }));
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => {
                    const artikel = filterOptions.artikel.find((a) => a.artikel === val);
                    return <Chip key={val} label={artikel?.name || val} size="small" />;
                  })}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 400 },
                },
              }}
            >
              {filterOptions.artikel.map((artikel) => (
                <MenuItem key={artikel.artikel} value={artikel.artikel}>
                  {artikel.name} ({artikel.artikel})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Status</InputLabel>
            <Select<string[]>
              multiple
              value={filterState.custom?.vertrag_sts || []}
              onChange={(event: SelectChangeEvent<string[]>) => {
                const value = event.target.value;
                const statusArray = typeof value === 'string' ? value.split(',') : value;
                setFilterState((prev) => ({
                  ...prev,
                  custom: {
                    ...prev.custom,
                    vertrag_sts: statusArray.length > 0 ? statusArray : undefined,
                  },
                }));
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => (
                    <Chip key={val} label={val} size="small" />
                  ))}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 400 },
                },
              }}
            >
              <MenuItem value="V">V - Verweigert</MenuItem>
              <MenuItem value="L">L - Laufend</MenuItem>
              <MenuItem value="W">W - Widerrufen</MenuItem>
              <MenuItem value="B">B - Beendet</MenuItem>
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            size="small"
            options={filterOptions.banner}
            value={filterState.custom?.banner || []}
            onChange={(_, newValue) => {
              setFilterState((prev) => ({
                ...prev,
                custom: {
                  ...prev.custom,
                  banner: newValue.length > 0 ? newValue : undefined,
                },
              }));
            }}
            renderInput={(params) => <TextField {...params} label="Banner" />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...chipProps } = getTagProps({ index });
                return <Chip key={key} label={option} size="small" {...chipProps} />;
              })
            }
            sx={{ minWidth: 250 }}
            ListboxProps={{
              style: { maxHeight: 500 },
            }}
          />
          <Autocomplete
            multiple
            size="small"
            options={filterOptions.werbeaktionen}
            value={filterState.custom?.werbeaktion_wbkz || []}
            onChange={(_, newValue) => {
              setFilterState((prev) => ({
                ...prev,
                custom: {
                  ...prev.custom,
                  werbeaktion_wbkz: newValue.length > 0 ? newValue : undefined,
                },
              }));
            }}
            renderInput={(params) => <TextField {...params} label="WKZ (Kampagne)" />}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...chipProps } = getTagProps({ index });
                return <Chip key={key} label={option} size="small" {...chipProps} />;
              })
            }
            sx={{ minWidth: 200 }}
            ListboxProps={{
              style: { maxHeight: 400 },
            }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Abo-Typ</InputLabel>
            <Select<string[]>
              multiple
              value={filterState.custom?.subscription_type || []}
              onChange={(event: SelectChangeEvent<string[]>) => {
                const value = event.target.value;
                const typeArray = typeof value === 'string' ? value.split(',') : value;
                setFilterState((prev) => ({
                  ...prev,
                  custom: {
                    ...prev.custom,
                    subscription_type: typeArray.length > 0 ? typeArray : undefined,
                  },
                }));
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => (
                    <Chip key={val} label={val} size="small" />
                  ))}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 400 },
                },
              }}
            >
              <MenuItem value="upsell">Upsell</MenuItem>
              <MenuItem value="quarterly">Quartalsweise</MenuItem>
              <MenuItem value="testphase">Testphase</MenuItem>
              <MenuItem value="einmalfaktura">Einmalfaktura</MenuItem>
              <MenuItem value="unbezahlt">Unbezahlt</MenuItem>
              <MenuItem value="none">Keine</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Quelle</InputLabel>
            <Select<string[]>
              multiple
              value={filterState.custom?.source || []}
              onChange={(event: SelectChangeEvent<string[]>) => {
                const value = event.target.value;
                const sourceArray = typeof value === 'string' ? value.split(',') : value;
                setFilterState((prev) => ({
                  ...prev,
                  custom: {
                    ...prev.custom,
                    source: sourceArray.length > 0 ? sourceArray : undefined,
                  },
                }));
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => (
                    <Chip key={val} label={val} size="small" />
                  ))}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 400 },
                },
              }}
            >
              {(filterOptions.sources ?? []).map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Blog</InputLabel>
            <Select<string[]>
              multiple
              value={filterState.custom?.blog || []}
              onChange={(event: SelectChangeEvent<string[]>) => {
                const value = event.target.value;
                const blogArray = typeof value === 'string' ? value.split(',') : value;
                setFilterState((prev) => ({
                  ...prev,
                  custom: {
                    ...prev.custom,
                    blog: blogArray.length > 0 ? blogArray : undefined,
                  },
                }));
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => (
                    <Chip key={val} label={val} size="small" />
                  ))}
                </Box>
              )}
              MenuProps={{
                PaperProps: {
                  style: { maxHeight: 400 },
                },
              }}
            >
              <MenuItem value="Finanztrends">Finanztrends</MenuItem>
              <MenuItem value="Börse-Global">Börse-Global</MenuItem>
              <MenuItem value="Börse-Express">Börse-Express</MenuItem>
              <MenuItem value="Stock-World">Stock-World</MenuItem>
              <MenuItem value="Trading-Treff">Trading-Treff</MenuItem>
              <MenuItem value="ITM">ITM</MenuItem>
              <MenuItem value="Unbekannt">Unbekannt</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Banner enthält"
            size="small"
            value={filterState.custom?.banner_contains || ''}
            onChange={(e) => {
              setFilterState((prev) => ({
                ...prev,
                custom: {
                  ...prev.custom,
                  banner_contains: e.target.value.trim() !== '' ? e.target.value : undefined,
                },
              }));
            }}
            sx={{ minWidth: 200 }}
            placeholder="z.B. SAM"
          />
          <TextField
            label="Banner enthält nicht"
            size="small"
            value={filterState.custom?.banner_not_contains || ''}
            onChange={(e) => {
              setFilterState((prev) => ({
                ...prev,
                custom: {
                  ...prev.custom,
                  banner_not_contains: e.target.value.trim() !== '' ? e.target.value : undefined,
                },
              }));
            }}
            sx={{ minWidth: 200 }}
            placeholder="z.B. TEST"
          />
        </Box>
      </LocalizationProvider>

      {/* Cache Information Section */}
      <Box
        sx={{
          mb: 3,
          mt: 3,
          p: 2,
          bgcolor: colors.primary[400],
          borderRadius: 1,
          border: `1px solid ${colors.blueAccent[700]}`,
        }}
      >
        <Typography variant="body2" fontWeight="bold" mb={1} sx={{ color: colors.blueAccent[300] }}>
          ℹ️ Umsatz-Berechnungen & Cache-System
        </Typography>
        <Typography variant="body2" sx={{ color: colors.grey[100] }}>
          <strong>Umsatz</strong> in Kacheln und Tabellen ist das tatsächlich eingegangene Geld:
          bezahlte Rechnungen, Gutschriften abgezogen. Eine Testphasen-Rechnung bis 1 € zählt nicht als Zahlung.
        </Typography>
        <Typography variant="body2" mt={1} sx={{ color: colors.grey[100] }}>
          <strong>Anfo-Wert</strong> ist dagegen ein Modellwert je Anfo — der erwartete Umsatz
          einer Anfo über 36 Monate, brutto (inkl. MwSt.): ihre Zahlung hochgerechnet, vorher der
          Wert ihres Angebots. Eine Prognose, kein Zahlungseingang.
        </Typography>
        <Typography variant="body2" mt={1} sx={{ color: colors.grey[100] }}>
          Laufzeiten und Retention-Kennzahlen kommen aus der Cache-Tabelle{' '}
          <strong>plx_order_metrics</strong> (aktualisiert alle 6 Stunden).
        </Typography>
      </Box>

      {/* Summary Cards */}
      {loading ? (
        <LoadingSpinner />
      ) : (
        stats && (
          <>
            {/* Primary KPIs */}
            <Grid container spacing={2} sx={{ mb: 3, mt: 2 }}>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Alle eingegangenen Bestellungen
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung:</strong> COUNT(*) aller Orders
                      </Typography>
                      <Typography variant="body2">
                        Beinhaltet alle Status: Verweigert (V), Laufend (L), Widerrufen (W), Beendet
                        (B)
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Bestellungen
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {stats.total_orders}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Testphase: {stats.trial_orders} | Unbezahlt:{' '}
                        {stats.accepted_unpaid_orders} | Quartalsabo: {stats.paid_quarterly} |
                        Jahresabo: {stats.paid_annual} | Einmalfaktura: {stats.one_time_orders} |
                        Verweigert: {stats.rejected}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Akzeptierte Bestellungen
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung:</strong> Bestellungen - Verweigert
                      </Typography>
                      <Typography variant="body2">
                        Anzahl der Orders, die nicht sofort blockiert wurden. Basis für
                        Aktivierungsrate.
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Anfos
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {stats.accepted_orders}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Testphase: {stats.accepted_trial_orders} | Unbezahlt:{' '}
                        {stats.accepted_unpaid_orders} | Quartalsabo:{' '}
                        {stats.accepted_paid_quarterly} | Jahresabo: {stats.accepted_paid_annual} |
                        Einmalfaktura: {stats.accepted_one_time_orders}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Aktive Bestellungen
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung:</strong> COUNT(Status = &apos;L&apos;)
                      </Typography>
                      <Typography variant="body2">
                        Bestellungen mit aktivem Status &quot;Laufend&quot;. Beinhaltet:
                      </Typography>
                      <Typography variant="body2" ml={1} mt={0.5}>
                        • Testphase (Frist läuft noch, keine Zahlung über 1 € — eine
                        Testphasen-Rechnung bis 1 € macht daraus kein bezahltes Abo)
                        <br />• Quartalsabo (quartalsweise Zahlung)
                        <br />• Jahresabo (jährliche Zahlung)
                        <br />• Einmalfaktura (einmalige Zahlung)
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Status Laufend
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {stats.active_orders}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Testphase: {stats.active_trial_orders} | Unbezahlt:{' '}
                        {stats.active_unpaid_orders} | Quartalsabo: {stats.active_paid_quarterly} |
                        Jahresabo: {stats.active_paid_annual} | Einmalfaktura:{' '}
                        {stats.active_one_time_orders}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Generierter Gesamtumsatz
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Quelle:</strong> Gecachte Metriken (aktualisiert alle 6 Stunden)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung nach Abo-Typ:</strong>
                      </Typography>
                      <Typography variant="body2" ml={1}>
                        • <strong>Quartalsabo:</strong> Tatsächlich bezahlte Rechnungen (Testphase +
                        90-Tage-Zyklen)
                        <br />• <strong>Jahresabo:</strong> Vorauszahlung für 1 Jahr (Quartalspreis
                        × 4)
                        <br />• <strong>Einmalfaktura:</strong> Einmalige Zahlung
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        Summe aller <strong>tatsächlich generierten</strong> Einnahmen basierend auf
                        bezahlten Rechnungen, Gutschriften abgezogen. Nur Status
                        &lsquo;Laufend&rsquo; (L) und &lsquo;Beendet&rsquo; (B) generieren Umsatz.
                        Eine Testphasen-Rechnung bis 1 € macht aus einer Bestellung kein bezahltes
                        Abo; der Betrag ist im Umsatz enthalten.
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Umsatz
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatEur(Number(stats.total_revenue))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        📊 + 📅 + 💶
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        {TERMS.anfoWert.label} gesamt
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Quelle:</strong> Summe der Anfo-Werte aller Anfos (je Anfo: Wert
                        ihrer Zahlung, sonst ihres Angebots, sonst ihres Produkts)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        {TERMS.anfoWert.tip}
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        <strong>Unterschied zum Umsatz:</strong> Dieser Wert ist eine Prognose
                        (Potenzial), „Umsatz“ zeigt tatsächlich erhaltene Zahlungen.
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        {TERMS.anfoWert.label}
                      </Typography>
                      <Typography variant="h3" color={colors.blueAccent[400]}>
                        {formatEur(Number(stats.expected_total_revenue || 0))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Prognose
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Ø {TERMS.anfoWert.label}
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Quelle:</strong> Durchschnitt des Anfo-Werts über die nicht
                        verweigerten Anfos mit Wert
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        {TERMS.anfoWert.tip}
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        <strong>Verwendung:</strong> Zur Bewertung von Werbeaktionen und
                        Traffic-Quellen unabhängig vom Zahlungszeitpunkt.
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Ø {TERMS.anfoWert.label}
                      </Typography>
                      <Typography variant="h3" color={colors.blueAccent[400]}>
                        {formatEur(Number(stats.avg_anfo_wert || 0))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Pro Anfo mit hinterlegtem Anfo-Wert
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Durchschnittlicher Quartalspreis (nur Abos)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung:</strong> SUM(quarterly_price) / COUNT(Bezahlt + Upsell)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Zähler:</strong> Summe der Quartalspreise für Bezahlt + Upsell
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Nenner:</strong> Anzahl der Abonnements (Bezahlt + Upsell)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Wichtig:</strong> Upsells (Jahresabos) verwenden denselben
                        quarterly_price Wert (NICHT × 4)
                      </Typography>
                      <Typography variant="body2">
                        <strong>Exkludiert:</strong> Einmalfaktura (keine wiederkehrenden Abos).
                        Zeigt vergleichbaren Preis pro Quartal unabhängig vom Abo-Modell (Quartal
                        vs. Jahr).
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Ø Quartalspreis
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatEur(Number(stats.average_quarterly_price || 0))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Normalisiert
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
            </Grid>

            {/* Secondary KPIs */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Durchschnittlicher Bestellwert (nur Abos, auf Jahresbasis)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung:</strong> SUM(Bezahlt: quarterly_price, Upsell:
                        quarterly_price × 4) / COUNT(Bezahlt + Upsell)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Zähler - Jahreswert der Abos:</strong>
                      </Typography>
                      <Typography variant="body2" ml={1} mb={1}>
                        • <strong>Bezahlt (Quartalsabos):</strong> quarterly_price × 1 (ein Quartal)
                        <br />• <strong>Upsell (Jahresabos):</strong> quarterly_price × 4 (vier
                        Quartale = 1 Jahr)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Nenner:</strong> Anzahl der Abonnements (Bezahlt + Upsell)
                      </Typography>
                      <Typography variant="body2">
                        <strong>Exkludiert:</strong> Einmalfaktura (keine wiederkehrenden Abos).
                        Zeigt den durchschnittlichen initialen Bestellwert bei Abo-Kunden.
                        Unterscheidet sich vom Ø Umsatz je zahlende Anfo durch den Fokus auf die
                        initiale Zahlung statt des bisher insgesamt bezahlten Umsatzes.
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Ø Bestellwert
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatEur(Number(stats.average_order_value || 0))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Initialer Wert
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Ø Umsatz je zahlende Anfo (bisher)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Quelle:</strong> Gecachte Umsatzdaten (total_revenue_generated)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung:</strong> Gesamtumsatz aller bezahlten Rechnungen /
                        Anzahl Kunden
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        Zeigt den <strong>tatsächlich realisierten</strong> Umsatz pro Kunde über
                        gesamte Laufzeit:
                      </Typography>
                      <Typography variant="body2" ml={1}>
                        • <strong>Quartalsabos:</strong> Alle bezahlten Quartalsrechnungen (nach
                        Testphase)
                        <br />• <strong>Jahresabos:</strong> Vorauszahlung + Verlängerungen
                        <br />• <strong>Einmalfakturas:</strong> Einmalzahlung
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        Höher als Bestellwert bei Quartalsabos durch langfristige Bindung und
                        mehrfache Zahlungen.
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Ø Umsatz je zahlende Anfo
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatEur(Number(stats.average_lifetime_value || 0))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Bisher bezahlt, keine Prognose
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Anteil gekündigter Abonnements
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung:</strong> Widerrufen / (Laufend + Widerrufen + Beendet) ×
                        100
                      </Typography>
                      <Typography variant="body2">
                        Zeigt wie viele gestartete Abos gekündigt oder nach Testphase nicht bezahlt
                        wurden. Niedrige Rate = hohe Kundenzufriedenheit.
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Widerrufsrate Abos
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatPct(Number(stats.churn_rate || 0))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Widerrufen ÷ gestartet, ohne Einmalfaktura
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Anteil direkt blockierter Bestellungen
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung:</strong> Verweigert / Gesamt Bestellungen × 100
                      </Typography>
                      <Typography variant="body2">
                        Orders, die sofort blockiert wurden (z.B. bereits existierendes Abo oder
                        Kündigung in letzten 90 Tagen). Hohe Rate = viele Duplikate.
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Ablehnungsrate
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatPct(Number(stats.rejection_rate || 0))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Direkt verweigert
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Erfolgsrate akzeptierter Orders
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung:</strong> Wiederkehrende Abonnenten / (Status L + W + B
                        ohne Einmalfaktura) × 100
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        Anteil der gestarteten Bestellungen (Laufend, Widerrufen, Beendet), die zu
                        wiederkehrenden Abonnenten wurden (Bezahlt, Upsell).
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Exkludiert:</strong> Einmalfaktura (keine wiederkehrenden Abos)
                      </Typography>
                      <Typography variant="body2">
                        <strong>Hinweis:</strong> Zählt alle Kunden, die jemals bezahlt haben - auch
                        wenn sie später gekündigt haben. Wichtiger Qualitätsindikator für
                        Lead-Quellen.
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Wandlungsrate
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatPct(Number(stats.conversion_rate || 0))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Testphase → Bezahlt
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Testphase beendet (unbezahlt)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung:</strong> COUNT(Status B mit Laufzeit ≤ Testphase-Tage)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        Bestellungen, die während der Testphase beendet wurden, ohne jemals zu
                        bezahlen (Status B mit Laufzeit ≤ 30 Tage).
                      </Typography>
                      <Typography variant="body2">
                        <strong>Unterschied zu anderen Status:</strong> Status V = vor Testphase
                        abgelehnt, Status W = nach Zahlung gekündigt.
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Testphase beendet
                      </Typography>
                      <Typography variant="h3" color={colors.redAccent[500]}>
                        {stats.trial_ended_unpaid}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Unbezahlt (Status B)
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Aktive Retention Rate
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung:</strong> Status L / (Status L + B) × 100, ohne
                        Einmalfaktura
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        Von allen Abos, die jemals aktiv wurden (L+B), wie viele sind noch aktiv
                        (L)?
                      </Typography>
                      <Typography variant="body2">
                        Zeigt Langzeit-Kundenbindung. Niedrige Werte → hohe Churn-Rate.
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Retention Rate Abos
                      </Typography>
                      <Typography variant="h3" color={colors.blueAccent[500]}>
                        {formatPct(Number(stats.active_retention_rate || 0))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Noch aktiv ÷ jemals aktiv, ohne Einmalfaktura
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Upsell-Quote (Anteil an allen ANFOs)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung:</strong> Jahresabos / Alle ANFOs (ohne Verweigerte) ×
                        100
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Zähler:</strong> Alle Upsell-Bestellungen (Jahresabos)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Nenner:</strong> Alle Anforderungen (ANFO) - Status L, B, W (ohne V
                        = Verweigert)
                      </Typography>
                      <Typography variant="body2">
                        Zeigt wie viele Kunden sich beim Bestellprozess für ein Jahresabo
                        entscheiden. Jahresabos haben keine Testphase und bringen höheren sofortigen
                        Umsatz.
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Upsell-Quote
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatPct(Number(stats.upsell_rate || 0))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Upsell-Anteil
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Upsell-Quote bei Zahlenden (Anteil an Bezahlenden)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung:</strong> Jahresabos / Zahlende Kunden (Bezahlt + Upsell)
                        × 100
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Zähler:</strong> Alle Upsell-Bestellungen (Jahresabos)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Nenner:</strong> Nur konvertierte Kunden (Bezahlt + Upsell)
                      </Typography>
                      <Typography variant="body2">
                        Zeigt von den Kunden, die überhaupt konvertiert sind, wie viele sich für ein
                        Jahresabo entschieden haben. Höhere Quote als normale Upsell-Quote, da nur
                        erfolgreiche Konversionen betrachtet werden.
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Upsell-Quote (Zahler)
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatPct(Number(stats.paid_upsell_rate || 0))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Von Bezahlenden
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
            </Grid>

            {/* Lifetime Metrics */}
            <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: colors.grey[100] }}>
              Lifetime-Kennzahlen
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} md={12}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Durchschnittliche aktuelle Lifetime (Tage)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung:</strong> AVG(subscription_lifetime_days) für
                        konvertierte Kunden
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Filter:</strong> zahlung_brutto &gt; 1 AND vertrag_sts IN (L, B)
                        ohne Einmalfaktura
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        • <strong>Aktive Abos (L):</strong> Tage von Bestellung bis heute
                        <br />• <strong>Beendete Abos (B):</strong> Tage von Bestellung bis
                        Kündigungsdatum
                      </Typography>
                      <Typography variant="body2">
                        Zeigt die durchschnittliche tatsächliche Verweildauer aller konvertierten
                        Kunden (bereits bezahlt).
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Ø Aktuelle Lifetime
                      </Typography>
                      <Typography variant="h3" color={colors.blueAccent[500]}>
                        {Number(stats.avg_current_lifetime_days || 0).toFixed(0)} Tage
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Tatsächliche Verweildauer
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
            </Grid>

            {/* Retention / VNR-Kennzahlen */}
            <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: colors.grey[100] }}>
              Retention / VNR-Kennzahlen
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              <Grid item xs={12} md={4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        AboCash (30 Tage) - Konversions- & Retention-Rate
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung (Prozentsatz):</strong>
                      </Typography>
                      <Typography variant="body2" ml={1} mb={1}>
                        <strong>Zähler:</strong> Anfos die bezahlt haben ({'>'}1€) UND ≥30 Tage
                        blieben
                        <br />
                        <strong>Nenner:</strong> ALLE Anfos ≥30 Tage alt (inkl. Testphase/Trial)
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        <strong>Inkludiert im Nenner:</strong>
                        <br />✓ Status L (Aktiv) - auch Testphase ohne Zahlung
                        <br />✓ Status B (Beendet) - auch wenn vor erster Zahlung
                        <br />✓ Status W (Widerrufen)
                        <br />
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        <strong>Exkludiert:</strong>
                        <br />✗ Status V (Abgelehnt) - nie gestartet
                        <br />✗ Anfos {'<'}30 Tage alt - noch nicht berechtigt
                        <br />✗ Einmalfaktura (einmalige Zahlungen, keine Abos)
                        <br />
                      </Typography>
                      <Typography variant="body2" mt={1} fontWeight="bold">
                        Misst: Von allen Anmeldungen, wie viele wurden zahlende Kunden (erste
                        Bezahlperiode)?
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        AboCash (30 Tage)
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {Number(stats.abo_cash_30_days_count || 0)} (
                        {formatPct(Number(stats.abo_cash_30_days_pct || 0))})
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        darunter Quartalsabo: {stats.quarterly_30d_retention || 0} | Jahresabo:{' '}
                        {stats.annual_30d_retention || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Abo2 (60 Tage) - Konversions- & Retention-Rate
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung (Prozentsatz):</strong>
                      </Typography>
                      <Typography variant="body2" ml={1} mb={1}>
                        <strong>Zähler:</strong> Anfos die bezahlt haben ({'>'}1€) UND ≥60 Tage
                        blieben
                        <br />
                        <strong>Nenner:</strong> ALLE Anfos ≥60 Tage alt (inkl. Testphase/Trial)
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        <strong>Inkludiert im Nenner:</strong>
                        <br />✓ Status L (Aktiv) - auch Testphase ohne Zahlung
                        <br />✓ Status B (Beendet) - auch wenn vor erster Zahlung
                        <br />✓ Status W (Widerrufen)
                        <br />
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        <strong>Exkludiert:</strong>
                        <br />✗ Status V (Abgelehnt) - nie gestartet
                        <br />✗ Anfos {'<'}60 Tage alt - noch nicht berechtigt
                        <br />✗ Einmalfaktura (einmalige Zahlungen, keine Abos)
                        <br />
                      </Typography>
                      <Typography variant="body2" mt={1} fontWeight="bold">
                        Misst: Mittelfristige Konversion & Kundenbindung (2 Monate)
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Abo2 (60 Tage)
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {Number(stats.abo_2_60_days_count || 0)} (
                        {formatPct(Number(stats.abo_2_60_days_pct || 0))})
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        darunter Quartalsabo: {stats.quarterly_60d_retention || 0} | Jahresabo:{' '}
                        {stats.annual_60d_retention || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
              <Grid item xs={12} md={4}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Abo4 (120 Tage) - Konversions- & Retention-Rate
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung (Prozentsatz):</strong>
                      </Typography>
                      <Typography variant="body2" ml={1} mb={1}>
                        <strong>Zähler:</strong> Anfos die bezahlt haben ({'>'}1€) UND ≥120 Tage
                        blieben
                        <br />
                        <strong>Nenner:</strong> ALLE Anfos ≥120 Tage alt (inkl. Testphase/Trial)
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        <strong>Inkludiert im Nenner:</strong>
                        <br />✓ Status L (Aktiv) - auch Testphase ohne Zahlung
                        <br />✓ Status B (Beendet) - auch wenn vor erster Zahlung
                        <br />✓ Status W (Widerrufen)
                        <br />
                      </Typography>
                      <Typography variant="body2" mt={1}>
                        <strong>Exkludiert:</strong>
                        <br />✗ Status V (Abgelehnt) - nie gestartet
                        <br />✗ Anfos {'<'}120 Tage alt - noch nicht berechtigt
                        <br />✗ Einmalfaktura (einmalige Zahlungen, keine Abos)
                        <br />
                      </Typography>
                      <Typography variant="body2" mt={1} fontWeight="bold">
                        Misst: Langfristige Kundenloyalität (4 Monate)
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Abo4 (120 Tage)
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {Number(stats.abo_4_120_days_count || 0)} (
                        {formatPct(Number(stats.abo_4_120_days_pct || 0))})
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        darunter Quartalsabo: {stats.quarterly_120d_retention || 0} | Jahresabo:{' '}
                        {stats.annual_120d_retention || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
            </Grid>

            {/* Subscription Type Breakdown */}
            <Typography
              variant="h6"
              fontWeight="bold"
              mb={2}
              mt={4}
              sx={{ color: colors.grey[100] }}
            >
              Retention nach Abo-Typ (Quarterly / Annual)
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {/* AboCash Quarterly */}
              <Grid item xs={12} md={2}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        AboCash Quartalsabo (30 Tage)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung (Prozentsatz):</strong>
                      </Typography>
                      <Typography variant="body2" ml={1} mb={1}>
                        <strong>Zähler:</strong> Quartalsabo-Anfos die bezahlt haben ({'>'}1€) UND
                        ≥30 Tage blieben
                        <br />
                        <strong>Nenner:</strong> ALLE Quartalsabo-Anfos ≥30 Tage alt
                        <br />
                        <strong>Exkludiert:</strong> Einmalfaktura (einmalige Zahlungen)
                      </Typography>
                      <Typography variant="body2" mt={1} fontWeight="bold">
                        Misst: Retention-Rate innerhalb Quartalsabos (erste Bezahlperiode)
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        AboCash Q (30d)
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatPct(Number(stats.abo_cash_30_days_quarterly_pct || 0))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>

              {/* AboCash Annual */}
              <Grid item xs={12} md={2}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        AboCash Jahresabo (30 Tage)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung (Prozentsatz):</strong>
                      </Typography>
                      <Typography variant="body2" ml={1} mb={1}>
                        <strong>Zähler:</strong> Jahresabo-Anfos die bezahlt haben ({'>'}1€) UND ≥30
                        Tage blieben
                        <br />
                        <strong>Nenner:</strong> ALLE Jahresabo-Anfos ≥30 Tage alt
                        <br />
                        <strong>Exkludiert:</strong> Einmalfaktura (einmalige Zahlungen)
                      </Typography>
                      <Typography variant="body2" mt={1} fontWeight="bold">
                        Misst: Retention-Rate innerhalb Jahresabos (erste Bezahlperiode)
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        AboCash U (30d)
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatPct(Number(stats.abo_cash_30_days_upsell_pct || 0))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>

              {/* Abo2 Quarterly */}
              <Grid item xs={12} md={2}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Abo2 Quartalsabo (60 Tage)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung (Prozentsatz):</strong>
                      </Typography>
                      <Typography variant="body2" ml={1} mb={1}>
                        <strong>Zähler:</strong> Quartalsabo-Anfos die bezahlt haben ({'>'}1€) UND
                        ≥60 Tage blieben
                        <br />
                        <strong>Nenner:</strong> ALLE Quartalsabo-Anfos ≥60 Tage alt
                        <br />
                        <strong>Exkludiert:</strong> Einmalfaktura (einmalige Zahlungen)
                      </Typography>
                      <Typography variant="body2" mt={1} fontWeight="bold">
                        Misst: Retention-Rate innerhalb Quartalsabos (2 Quartale)
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Abo2 Q (60d)
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatPct(Number(stats.abo_2_60_days_quarterly_pct || 0))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>

              {/* Abo2 Annual */}
              <Grid item xs={12} md={2}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Abo2 Jahresabo (60 Tage)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung (Prozentsatz):</strong>
                      </Typography>
                      <Typography variant="body2" ml={1} mb={1}>
                        <strong>Zähler:</strong> Jahresabo-Anfos die bezahlt haben ({'>'}1€) UND ≥60
                        Tage blieben
                        <br />
                        <strong>Nenner:</strong> ALLE Jahresabo-Anfos ≥60 Tage alt
                        <br />
                        <strong>Exkludiert:</strong> Einmalfaktura (einmalige Zahlungen)
                      </Typography>
                      <Typography variant="body2" mt={1} fontWeight="bold">
                        Misst: Retention-Rate innerhalb Jahresabos (2 Quartale)
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Abo2 U (60d)
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatPct(Number(stats.abo_2_60_days_upsell_pct || 0))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>

              {/* Abo4 Quarterly */}
              <Grid item xs={12} md={2}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Abo4 Quartalsabo (120 Tage)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung (Prozentsatz):</strong>
                      </Typography>
                      <Typography variant="body2" ml={1} mb={1}>
                        <strong>Zähler:</strong> Quartalsabo-Anfos die bezahlt haben ({'>'}1€) UND
                        ≥120 Tage blieben
                        <br />
                        <strong>Nenner:</strong> ALLE Quartalsabo-Anfos ≥120 Tage alt
                        <br />
                        <strong>Exkludiert:</strong> Einmalfaktura (einmalige Zahlungen)
                      </Typography>
                      <Typography variant="body2" mt={1} fontWeight="bold">
                        Misst: Retention-Rate innerhalb Quartalsabos (4 Quartale)
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Abo4 Q (120d)
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatPct(Number(stats.abo_4_120_days_quarterly_pct || 0))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>

              {/* Abo4 Annual */}
              <Grid item xs={12} md={2}>
                <Tooltip
                  title={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2" fontWeight="bold" mb={1}>
                        Abo4 Jahresabo (120 Tage)
                      </Typography>
                      <Typography variant="body2" mb={1}>
                        <strong>Berechnung (Prozentsatz):</strong>
                      </Typography>
                      <Typography variant="body2" ml={1} mb={1}>
                        <strong>Zähler:</strong> Jahresabo-Anfos die bezahlt haben ({'>'}1€) UND
                        ≥120 Tage blieben
                        <br />
                        <strong>Nenner:</strong> ALLE Jahresabo-Anfos ≥120 Tage alt
                        <br />
                        <strong>Exkludiert:</strong> Einmalfaktura (einmalige Zahlungen)
                      </Typography>
                      <Typography variant="body2" mt={1} fontWeight="bold">
                        Misst: Retention-Rate innerhalb Jahresabos (4 Quartale)
                      </Typography>
                    </Box>
                  }
                  placement="top"
                  arrow
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Abo4 U (120d)
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {formatPct(Number(stats.abo_4_120_days_upsell_pct || 0))}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
            </Grid>

            {/* Upsell Payment Retention Metrics (All Anfos: L, B, W) */}
            <Typography
              variant="h6"
              fontWeight="bold"
              mb={2}
              mt={4}
              sx={{ color: colors.grey[100] }}
            >
              Upsell Zahlungs-Retention (Alle Anfos)
            </Typography>
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {/* Upsell Year 1 Paid */}
              <Grid item xs={12} sm={6} md={2}>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Anzahl aller Jahresabos (inkl. L, B, W), die ihre erste Jahresrechnung
                        tatsächlich bezahlt haben.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Berechnung:</strong> payment_number=1, actually_paid=1
                      </Typography>
                      <Typography variant="body2">
                        Beinhaltet alle Status: Laufend, Beendet, Widerrufen
                      </Typography>
                    </Box>
                  }
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Jahr 1 Bezahlt
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {Number(stats.upsells_paid_year_1 || 0)}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Bezahlt: {stats.upsells_paid_year_1 || 0} | Unbezahlt:{' '}
                        {stats.upsells_unpaid_year_1 || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>

              {/* Upsell Year 1 Unpaid */}
              <Grid item xs={12} sm={6} md={2}>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Jahresabos, die ihre erste Rechnung NICHT bezahlt haben.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Berechnung:</strong> payment_number=1, actually_paid=0
                      </Typography>
                      <Typography variant="body2">
                        Kunden, die sich angemeldet aber nie bezahlt haben.
                      </Typography>
                    </Box>
                  }
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Jahr 1 Unbezahlt
                      </Typography>
                      <Typography variant="h3" color={colors.redAccent[500]}>
                        {Number(stats.upsells_unpaid_year_1 || 0)}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Keine Zahlung erhalten
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>

              {/* Upsell Year 2 Paid */}
              <Grid item xs={12} sm={6} md={2}>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Jahresabos, die ihre zweite Jahresrechnung (Erneuerung) bezahlt haben -
                        unabhängig vom Status.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Berechnung:</strong> payment_number=2, actually_paid=1
                      </Typography>
                      <Typography variant="body2">
                        Erfolgreiche Verlängerungen nach dem ersten Jahr.
                      </Typography>
                    </Box>
                  }
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Jahr 2 Bezahlt
                      </Typography>
                      <Typography variant="h3" color={colors.greenAccent[500]}>
                        {Number(stats.upsells_paid_year_2 || 0)}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        Verlängerungen erfolgreich
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>

              {/* Year 1 Payment Rate */}
              <Grid item xs={12} sm={6} md={2}>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Anteil aller Upsells, die ihre erste Jahresrechnung bezahlt haben.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Formel:</strong> (Bezahlte Jahr 1 / Alle Upsells) × 100
                      </Typography>
                      <Typography variant="body2">
                        Zeigt die initiale Zahlungsbereitschaft der Kunden.
                      </Typography>
                    </Box>
                  }
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Jahr 1 Quote
                      </Typography>
                      <Typography variant="h3" color={colors.blueAccent[500]}>
                        {formatPct(Number(stats.upsell_year_1_payment_rate || 0))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        {stats.upsells_paid_year_1 || 0} / {stats.total_upsells || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>

              {/* Year 2 Payment Rate (from total) */}
              <Grid item xs={12} sm={6} md={2}>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Anteil aller Upsells, die bis zur zweiten Jahresrechnung durchgehalten
                        haben.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Formel:</strong> (Bezahlte Jahr 2 / Alle Upsells) × 100
                      </Typography>
                      <Typography variant="body2">
                        Gesamtretention von Anmeldung bis Jahr 2.
                      </Typography>
                    </Box>
                  }
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Jahr 2 Quote (Gesamt)
                      </Typography>
                      <Typography variant="h3" color={colors.blueAccent[500]}>
                        {formatPct(Number(stats.upsell_year_2_from_total_rate || 0))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        {stats.upsells_paid_year_2 || 0} / {stats.total_upsells || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>

              {/* Year 2 Payment Rate (from payers) */}
              <Grid item xs={12} sm={6} md={2}>
                <Tooltip
                  title={
                    <Box>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Verlängerungsrate von Kunden, die bereits Jahr 1 bezahlt haben.
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Formel:</strong> (Bezahlte Jahr 2 / Bezahlte Jahr 1) × 100
                      </Typography>
                      <Typography variant="body2">
                        Echte Retention von zahlenden Kunden zu Verlängerungen.
                      </Typography>
                    </Box>
                  }
                >
                  <Card elevation={2} sx={{ cursor: 'help' }}>
                    <CardContent>
                      <Typography color={colors.grey[100]} variant="h6" fontWeight="600">
                        Jahr 2 Quote (Zahler)
                      </Typography>
                      <Typography variant="h3" color={colors.blueAccent[500]}>
                        {formatPct(Number(stats.upsell_year_2_from_payers_rate || 0))}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]}>
                        {stats.upsells_paid_year_2 || 0} / {stats.upsells_paid_year_1 || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Tooltip>
              </Grid>
            </Grid>
          </>
        )
      )}

      {/* Visualizations Section */}
      {vizLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <LoadingSpinner />
        </Box>
      ) : (
        <>
          <Typography
            variant="h5"
            fontWeight="bold"
            mt={4}
            mb={3}
            sx={{ color: colors.greenAccent[500] }}
          >
            Visualisierungen
          </Typography>

          {/* Row 1: Timeline Charts with Aggregation Toggle */}
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
            >
              <Tooltip title="Zeigt die Entwicklung von Bestellungen und Umsatz über die Zeit. Umsatz beinhaltet nur bezahlte Abos (Quartalsabo, Jahresabo, Einmalfaktura).">
                <Typography variant="h6" fontWeight="bold" sx={{ color: colors.grey[100] }}>
                  Bestellungen & Umsatz Timeline
                </Typography>
              </Tooltip>
              <ToggleButtonGroup
                value={aggregation}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue !== null) setAggregation(newValue);
                }}
                size="small"
              >
                <ToggleButton value="daily">Täglich</ToggleButton>
                <ToggleButton value="weekly">Wöchentlich</ToggleButton>
                <ToggleButton value="monthly">Monatlich</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      mb={1}
                      sx={{ color: colors.grey[100] }}
                    >
                      Bestellungen
                    </Typography>
                    <Box sx={{ height: 350 }}>
                      <ResponsiveLine
                        data={[
                          {
                            id: 'Bestellungen',
                            data: timelineData.map((d) => ({ x: d.date, y: d.order_count })),
                          },
                        ]}
                        margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
                        xScale={{ type: 'point' }}
                        yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                        axisBottom={{
                          tickRotation: -45,
                          legend: 'Datum',
                          legendOffset: 40,
                          legendPosition: 'middle',
                        }}
                        axisLeft={{
                          legend: 'Anzahl Bestellungen',
                          legendOffset: -50,
                          legendPosition: 'middle',
                        }}
                        pointSize={6}
                        pointBorderWidth={2}
                        pointBorderColor={{ from: 'serieColor' }}
                        colors={{ scheme: 'category10' }}
                        useMesh={true}
                        tooltip={({ point }) => (
                          <Box
                            sx={{
                              background: colors.primary[400],
                              p: 1,
                              borderRadius: 1,
                              color: colors.grey[100],
                              whiteSpace: 'pre-line',
                            }}
                          >
                            {`Datum: ${point.data.x}\nBestellungen: ${point.data.y}`}
                          </Box>
                        )}
                        theme={{
                          text: { fill: colors.grey[100] },
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card elevation={2}>
                  <CardContent>
                    <Tooltip
                      title={
                        <Box sx={{ p: 1 }}>
                          <Typography variant="body2" fontWeight="bold" mb={1}>
                            Umsatz über Zeit
                          </Typography>
                          <Typography variant="body2" mb={1}>
                            <strong>Quelle:</strong> Gecachte Metriken (total_revenue_generated),
                            aktualisiert alle 6 Stunden
                          </Typography>
                          <Typography variant="body2" mb={1}>
                            Zeigt tatsächlich generierte Einnahmen aus bezahlten Rechnungen
                            gruppiert nach Erstellungsdatum. Berücksichtigt:
                          </Typography>
                          <Typography variant="body2" ml={1}>
                            • Quartalsabos: Testphasen und 90-Tage-Abrechnungszyklen
                            <br />• Jahresabos: Vorauszahlungen (4× Quartalspreis)
                            <br />• Einmalfakturas: Einmalzahlungen
                          </Typography>
                          <Typography variant="body2" mt={1}>
                            Nur Orders mit Status &lsquo;Laufend&rsquo; (L) und
                            &lsquo;Beendet&rsquo; (B) generieren Umsatz.
                          </Typography>
                        </Box>
                      }
                      placement="top"
                      arrow
                    >
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        mb={1}
                        sx={{ color: colors.grey[100], cursor: 'help' }}
                      >
                        Umsatz
                      </Typography>
                    </Tooltip>
                    <Box sx={{ height: 350 }}>
                      <ResponsiveLine
                        data={[
                          {
                            id: 'Umsatz',
                            data: timelineData.map((d) => ({ x: d.date, y: Number(d.revenue) })),
                          },
                        ]}
                        margin={{ top: 20, right: 20, bottom: 50, left: 60 }}
                        xScale={{ type: 'point' }}
                        yScale={{ type: 'linear', min: 'auto', max: 'auto' }}
                        axisBottom={{
                          tickRotation: -45,
                          legend: 'Datum',
                          legendOffset: 40,
                          legendPosition: 'middle',
                        }}
                        axisLeft={{
                          legend: 'Umsatz (€)',
                          legendOffset: -50,
                          legendPosition: 'middle',
                        }}
                        pointSize={6}
                        pointBorderWidth={2}
                        pointBorderColor={{ from: 'serieColor' }}
                        colors={{ scheme: 'set1' }}
                        useMesh={true}
                        tooltip={({ point }) => (
                          <Box
                            sx={{
                              background: colors.primary[400],
                              p: 1,
                              borderRadius: 1,
                              color: colors.grey[100],
                              whiteSpace: 'pre-line',
                            }}
                          >
                            {`Datum: ${point.data.x}\nUmsatz: ${formatEur(Number(point.data.y))}`}
                          </Box>
                        )}
                        theme={{
                          text: { fill: colors.grey[100] },
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          {/* Row 2: Product Performance - Two Charts Side by Side */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Tooltip title="Zeigt die Anzahl der Bestellungen pro Produkt mit Conversion Rate (Anteil bezahlter Abos).">
                <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: colors.grey[100] }}>
                  Bestellungen nach Produkt
                </Typography>
              </Tooltip>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ height: 400 }}>
                    <ResponsiveBar
                      data={productPerformanceData.map((p) => ({
                        product: p.product_name,
                        orders: p.total_orders,
                        conversionRate: p.conversion_rate,
                      }))}
                      keys={['orders']}
                      indexBy="product"
                      margin={{ top: 20, right: 130, bottom: 100, left: 60 }}
                      padding={0.3}
                      layout="horizontal"
                      valueScale={{ type: 'linear' }}
                      indexScale={{ type: 'band', round: true }}
                      colors={{ scheme: 'nivo' }}
                      axisBottom={{
                        legend: 'Anzahl Bestellungen',
                        legendPosition: 'middle',
                        legendOffset: 40,
                      }}
                      axisLeft={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                      }}
                      labelSkipWidth={12}
                      labelSkipHeight={12}
                      label={(d) => `${d.value} (${formatPct(Number(d.data.conversionRate))})`}
                      labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                      tooltip={({ data, value }) => (
                        <Box
                          sx={{
                            background: colors.primary[400],
                            p: 1,
                            borderRadius: 1,
                            color: colors.grey[100],
                          }}
                        >
                          <>
                            <div>
                              <strong>Produkt:</strong> {data.product}
                            </div>
                            <div>
                              <strong>Bestellungen:</strong> {value}
                            </div>
                            <div>
                              <strong>Conversion Rate:</strong>{' '}
                              {formatPct(Number(data.conversionRate))}
                            </div>
                          </>
                        </Box>
                      )}
                      theme={{
                        text: { fill: colors.grey[100] },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Row 3: Subscription Distribution + Retention Curve */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Tooltip title="Bestellungen mit Status Laufend oder Beendet, nach Abo-Typ. Zeigt Anzahl und bezahlten Umsatz je Typ. Testphasen haben noch nicht gezahlt.">
                <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: colors.grey[100] }}>
                  Status Laufend & Beendet nach Typ
                </Typography>
              </Tooltip>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ height: 400 }}>
                    <ResponsivePie
                      data={subscriptionDistribution.map((s) => ({
                        id: s.subscription_type,
                        label: s.subscription_type,
                        value: s.count,
                        revenue: Number(s.revenue),
                      }))}
                      margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                      innerRadius={0.5}
                      padAngle={0.7}
                      cornerRadius={3}
                      activeOuterRadiusOffset={8}
                      colors={{ scheme: 'paired' }}
                      borderWidth={1}
                      borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                      arcLinkLabelsSkipAngle={10}
                      arcLinkLabelsTextColor={colors.grey[100]}
                      arcLinkLabelsThickness={2}
                      arcLinkLabelsColor={{ from: 'color' }}
                      arcLabelsSkipAngle={10}
                      arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                      tooltip={({ datum }) => (
                        <Box
                          sx={{
                            background: colors.primary[400],
                            p: 1,
                            borderRadius: 1,
                            color: colors.grey[100],
                          }}
                        >
                          <>
                            <div>
                              <strong>{datum.label}</strong>
                            </div>
                            <div>Anzahl: {datum.value}</div>
                            <div>Umsatz: {formatEur(datum.data.revenue)}</div>
                          </>
                        </Box>
                      )}
                      theme={{
                        text: { fill: colors.grey[100] },
                        tooltip: {
                          container: {
                            background: colors.primary[400],
                            color: colors.grey[100],
                          },
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Tooltip title="Zeigt die Retention-Rate aller akzeptierten Anfos (ohne Verweigert) über verschiedene Zeiträume. Prozentsatz ALLER Orders im gewählten Zeitraum, die nach X Tagen noch als aktive Abonnements laufen (Status Laufend oder Beendet mit Laufzeit >= X Tage). Die Kurve sollte kontinuierlich fallen oder gleich bleiben.">
                <Typography variant="h6" fontWeight="bold" mb={2} sx={{ color: colors.grey[100] }}>
                  Retention Curve (alle Anfos)
                </Typography>
              </Tooltip>
              <Card elevation={2}>
                <CardContent>
                  <Box sx={{ height: 400 }}>
                    <ResponsiveLine
                      data={[
                        {
                          id: 'Retention %',
                          data: retentionCurveData.map((r) => ({
                            x: r.days,
                            y: Number(r.retention_percentage || 0),
                          })),
                        },
                      ]}
                      margin={{ top: 20, right: 110, bottom: 50, left: 60 }}
                      xScale={{ type: 'linear', min: 0, max: 365 }}
                      yScale={{ type: 'linear', min: 0, max: 100 }}
                      axisBottom={{
                        legend: 'Tage seit Bestellung',
                        legendOffset: 40,
                        legendPosition: 'middle',
                      }}
                      axisLeft={{
                        legend: 'Retention (%)',
                        legendOffset: -50,
                        legendPosition: 'middle',
                      }}
                      pointSize={8}
                      pointBorderWidth={2}
                      pointBorderColor={{ from: 'serieColor' }}
                      enableArea={true}
                      areaOpacity={0.2}
                      useMesh={true}
                      tooltip={({ point }) => (
                        <Box
                          sx={{
                            background: colors.primary[400],
                            p: 1,
                            borderRadius: 1,
                            color: colors.grey[100],
                            whiteSpace: 'pre-line',
                          }}
                        >
                          {`${point.data.x} Tage\nRetention: ${formatPct(Number(point.data.y), 2)}`}
                        </Box>
                      )}
                      theme={{
                        text: { fill: colors.grey[100] },
                        tooltip: {
                          container: {
                            background: colors.primary[400],
                            color: colors.grey[100],
                          },
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Row 4: Monthly Revenue (Actual Payment Dates) */}
          <Box sx={{ mb: 4 }}>
            <Tooltip
              title={
                <Box sx={{ p: 1 }}>
                  <Typography variant="body2" fontWeight="bold" mb={1}>
                    Tatsächliche Monatsumsätze
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    Zeigt die tatsächlich erhaltenen Zahlungen gruppiert nach{' '}
                    <strong>Zahlungsmonat</strong> (nicht Bestellmonat).
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Wichtig:</strong> Jahresabos werden im Zahlungsmonat mit vollem Betrag
                    angezeigt (z.B. €1.500 im Januar), nicht auf 12 Monate verteilt.
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    <strong>Zahlungslogik:</strong>
                  </Typography>
                  <Typography variant="body2" ml={1}>
                    • <strong>Quartalsabo:</strong> Zahlung alle 90 Tage (nach Testphase)
                    <br />• <strong>Jahresabo:</strong> Zahlung alle 365 Tage (volle Summe =
                    Quartalspreis × 4)
                    <br />• <strong>Einmalfaktura:</strong> Einmalige Zahlung bei Bestellung
                  </Typography>
                  <Typography variant="body2" mt={1}>
                    <strong>Vorteil:</strong> Zeigt echten Cash Flow - Januarspitzen von
                    Jahresabo-Erneuerungen sind sichtbar, nicht versteckt.
                  </Typography>
                </Box>
              }
              placement="top"
              arrow
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                mb={2}
                sx={{ color: colors.grey[100], cursor: 'help' }}
              >
                Monatsumsätze (nach Zahlungsdatum)
              </Typography>
            </Tooltip>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ height: 400 }}>
                  <ResponsiveBar
                    data={monthlyRevenueData.map((d) => ({
                      month: d.month,
                      Quartalsabo: Number(d.quarterly_revenue),
                      Jahresabo: Number(d.annual_revenue),
                      Einmalfaktura: Number(d.onetime_revenue),
                    }))}
                    keys={['Quartalsabo', 'Jahresabo', 'Einmalfaktura']}
                    indexBy="month"
                    margin={{ top: 20, right: 150, bottom: 60, left: 80 }}
                    padding={0.3}
                    colors={{ scheme: 'paired' }}
                    axisBottom={{
                      tickRotation: -45,
                      legend: 'Monat (Zahlungsdatum)',
                      legendPosition: 'middle',
                      legendOffset: 50,
                    }}
                    axisLeft={{
                      legend: 'Umsatz (€)',
                      legendPosition: 'middle',
                      legendOffset: -60,
                    }}
                    legends={[
                      {
                        dataFrom: 'keys',
                        anchor: 'bottom-right',
                        direction: 'column',
                        translateX: 140,
                        itemWidth: 120,
                        itemHeight: 20,
                      },
                    ]}
                    tooltip={({ id, value, data }) => (
                      <Box
                        sx={{
                          background: colors.primary[400],
                          p: 1,
                          borderRadius: 1,
                          color: colors.grey[100],
                        }}
                      >
                        <div>
                          <strong>Monat:</strong> {data.month}
                        </div>
                        <div>
                          <strong>Typ:</strong> {id}
                        </div>
                        <div>
                          <strong>Umsatz:</strong> {formatEur(Number(value))}
                        </div>
                      </Box>
                    )}
                    theme={{
                      text: { fill: colors.grey[100] },
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Box>
        </>
      )}

      {/* Product Performance Table */}
      {!loading && productPerformanceData && productPerformanceData.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="600" sx={{ mb: 2, color: colors.grey[100] }}>
            Produkt-Performance
          </Typography>
          <Box
            sx={{
              backgroundColor: colors.primary[400],
              borderRadius: '4px',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <TableWidget<PlxProductPerformance & { upsells_formatted: string }>
              key={`produkte-${vizStand}`}
              getData={async () =>
                productPerformanceData.map((item) => ({
                  ...item,
                  upsells_formatted: `${item.upsells_count} (${formatPct(Number(item.upsells_rate))})`,
                }))
              }
              // Summenzeile aus dem Backend (dieselbe gefilterte Menge, jede Quote mit
              // ihrem eigenen Nenner). Bis 2026-09-22 wurde sie hier aus den Zeilenwerten
              // gemittelt, gewichtet mit round(total_orders * 0,9).
              computeSummary={
                productPerformanceSummary
                  ? () => ({
                      ...productPerformanceSummary,
                      upsells_formatted: `${productPerformanceSummary.upsells_count} (${formatPct(Number(productPerformanceSummary.upsells_rate))})`,
                    })
                  : undefined
              }
              detailsHeaderMapping={{
                product_name: { name: 'Produkt', type: 'string' },
                total_orders: { name: 'Bestellungen', type: 'number' },
                anteil: { name: 'Anteil', type: 'number', digits: 1, attach: ' %' },
                paid_orders: { name: 'Bezahlte Orders', type: 'number' },
                upsells_formatted: { name: 'Upsells', type: 'string' },
                total_revenue: { name: 'Umsatz', type: 'number', digits: 2, attach: ' €' },
                expected_revenue: {
                  name: TERMS.anfoWert.label,
                  type: 'number',
                  digits: 2,
                  attach: ' €',
                },
                expected_anfo_value: {
                  name: `Ø ${TERMS.anfoWert.label}`,
                  type: 'number',
                  digits: 2,
                  attach: ' €',
                },
                conversion_rate: { name: 'Wandlungsrate', type: 'number', digits: 1, attach: ' %' },
                conversion_rate_quarterly: {
                  name: 'W. Rate Quartalsw.',
                  type: 'number',
                  digits: 1,
                  attach: ' %',
                },
                conversion_rate_upsells: {
                  name: 'W. Rate Upsells',
                  type: 'number',
                  digits: 1,
                  attach: ' %',
                },
                churn_rate: { name: 'Widerrufsrate', type: 'number', digits: 1, attach: ' %' },
                retention_rate: { name: 'Retention Rate', type: 'number', digits: 1, attach: ' %' },
                abo_2_60_days: { name: 'Abo2 (60d)', type: 'number', digits: 1, attach: ' %' },
                abo_4_120_days: { name: 'Abo4 (120d)', type: 'number', digits: 1, attach: ' %' },
                abo_4_120_days_quarterly: {
                  name: 'Abo4 Q (120d)',
                  type: 'number',
                  digits: 1,
                  attach: ' %',
                },
                abo_4_120_days_upsell: {
                  name: 'Abo4 U (120d)',
                  type: 'number',
                  digits: 1,
                  attach: ' %',
                },
                avg_lifetime_value: {
                  name: 'Ø Umsatz je zahlende Anfo',
                  type: 'number',
                  digits: 2,
                  attach: ' €',
                },
                avg_current_lifetime_days: {
                  name: 'Ø Aktuelle Lifetime',
                  type: 'number',
                  digits: 0,
                  attach: ' Tage',
                },
              }}
              initialSortColumn="total_revenue"
              sortOrder="desc"
              excelDownloadName="plx-product-performance"
            />
          </Box>
        </Box>
      )}

      {/* Source Performance Table */}
      {!loading && sourcePerformanceData && sourcePerformanceData.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="600" sx={{ mb: 2, color: colors.grey[100] }}>
            Performance gruppiert nach Quelle
          </Typography>
          <Box
            sx={{
              backgroundColor: colors.primary[400],
              borderRadius: '4px',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <TableWidget<PlxSourcePerformance>
              key={`quellen-${vizStand}`}
              getData={async () => sourcePerformanceData}
              // Summenzeile aus dem Backend (dieselbe gefilterte Menge, jede Quote mit
              // ihrem eigenen Nenner). Bis 2026-09-22 wurde sie hier aus den Zeilenwerten
              // gemittelt, gewichtet mit round(total_orders * 0,9).
              computeSummary={sourcePerformanceSummary ? () => sourcePerformanceSummary : undefined}
              detailsHeaderMapping={{
                source: { name: 'Quelle', type: 'string' },
                total_orders: { name: 'Bestellungen', type: 'number' },
                anteil: { name: 'Anteil', type: 'number', digits: 1, attach: ' %' },
                paid_orders: { name: 'Bezahlte Orders', type: 'number' },
                total_revenue: { name: 'Umsatz', type: 'number', digits: 2, attach: ' €' },
                expected_revenue: {
                  name: TERMS.anfoWert.label,
                  type: 'number',
                  digits: 2,
                  attach: ' €',
                },
                expected_anfo_value: {
                  name: `Ø ${TERMS.anfoWert.label}`,
                  type: 'number',
                  digits: 2,
                  attach: ' €',
                },
                conversion_rate: { name: 'Wandlungsrate', type: 'number', digits: 1, attach: ' %' },
                churn_rate: { name: 'Widerrufsrate', type: 'number', digits: 1, attach: ' %' },
                retention_rate: { name: 'Retention Rate', type: 'number', digits: 1, attach: ' %' },
                abo_2_60_days: { name: 'Abo2 (60d)', type: 'number', digits: 1, attach: ' %' },
                abo_4_120_days: { name: 'Abo4 (120d)', type: 'number', digits: 1, attach: ' %' },
                abo_4_120_days_quarterly: {
                  name: 'Abo4 Q (120d)',
                  type: 'number',
                  digits: 1,
                  attach: ' %',
                },
                abo_4_120_days_upsell: {
                  name: 'Abo4 U (120d)',
                  type: 'number',
                  digits: 1,
                  attach: ' %',
                },
                avg_lifetime_value: {
                  name: 'Ø Umsatz je zahlende Anfo',
                  type: 'number',
                  digits: 2,
                  attach: ' €',
                },
                avg_current_lifetime_days: {
                  name: 'Ø Aktuelle Lifetime',
                  type: 'number',
                  digits: 0,
                  attach: ' Tage',
                },
              }}
              initialSortColumn="total_revenue"
              sortOrder="desc"
              excelDownloadName="plx-source-performance"
            />
          </Box>
        </Box>
      )}

      {/* Blog Performance Table */}
      {!loading && blogPerformanceData && blogPerformanceData.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="600" sx={{ mb: 2, color: colors.grey[100] }}>
            Performance gruppiert nach Blog
          </Typography>
          <Box
            sx={{
              backgroundColor: colors.primary[400],
              borderRadius: '4px',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <TableWidget<PlxBlogPerformance>
              key={`blogs-${vizStand}`}
              getData={async () => blogPerformanceData}
              // Summenzeile aus dem Backend (dieselbe gefilterte Menge, jede Quote mit
              // ihrem eigenen Nenner). Bis 2026-09-22 wurde sie hier aus den Zeilenwerten
              // gemittelt, gewichtet mit round(total_orders * 0,9).
              computeSummary={blogPerformanceSummary ? () => blogPerformanceSummary : undefined}
              detailsHeaderMapping={{
                blog: { name: 'Blog', type: 'string' },
                total_orders: { name: 'Bestellungen', type: 'number' },
                anteil: { name: 'Anteil', type: 'number', digits: 1, attach: ' %' },
                paid_orders: { name: 'Bezahlte Orders', type: 'number' },
                total_revenue: { name: 'Umsatz', type: 'number', digits: 2, attach: ' €' },
                expected_revenue: {
                  name: TERMS.anfoWert.label,
                  type: 'number',
                  digits: 2,
                  attach: ' €',
                },
                expected_anfo_value: {
                  name: `Ø ${TERMS.anfoWert.label}`,
                  type: 'number',
                  digits: 2,
                  attach: ' €',
                },
                conversion_rate: { name: 'Wandlungsrate', type: 'number', digits: 1, attach: ' %' },
                churn_rate: { name: 'Widerrufsrate', type: 'number', digits: 1, attach: ' %' },
                retention_rate: { name: 'Retention Rate', type: 'number', digits: 1, attach: ' %' },
                abo_2_60_days: { name: 'Abo2 (60d)', type: 'number', digits: 1, attach: ' %' },
                abo_4_120_days: { name: 'Abo4 (120d)', type: 'number', digits: 1, attach: ' %' },
                abo_4_120_days_quarterly: {
                  name: 'Abo4 Q (120d)',
                  type: 'number',
                  digits: 1,
                  attach: ' %',
                },
                abo_4_120_days_upsell: {
                  name: 'Abo4 U (120d)',
                  type: 'number',
                  digits: 1,
                  attach: ' %',
                },
                avg_lifetime_value: {
                  name: 'Ø Umsatz je zahlende Anfo',
                  type: 'number',
                  digits: 2,
                  attach: ' €',
                },
                avg_current_lifetime_days: {
                  name: 'Ø Aktuelle Lifetime',
                  type: 'number',
                  digits: 0,
                  attach: ' Tage',
                },
              }}
              initialSortColumn="total_revenue"
              sortOrder="desc"
              excelDownloadName="plx-blog-performance"
            />
          </Box>
        </Box>
      )}

      {/* WKZ Performance Table */}
      {!loading && wkzPerformanceData && wkzPerformanceData.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="600" sx={{ mb: 2, color: colors.grey[100] }}>
            WKZ-Performance
          </Typography>
          <Box
            sx={{
              backgroundColor: colors.primary[400],
              borderRadius: '4px',
              overflow: 'hidden',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <TableWidget<PlxWkzPerformance>
              key={`wkz-${vizStand}`}
              getData={async () => wkzPerformanceData}
              // Summenzeile aus dem Backend (dieselbe gefilterte Menge, jede Quote mit
              // ihrem eigenen Nenner). Bis 2026-09-22 wurde sie hier aus den Zeilenwerten
              // gemittelt, gewichtet mit round(total_orders * 0,9).
              computeSummary={wkzPerformanceSummary ? () => wkzPerformanceSummary : undefined}
              detailsHeaderMapping={{
                werbeaktion_wbkz: { name: 'Kampagne (WKZ)', type: 'string' },
                campaign_type: { name: 'Typ', type: 'string' },
                total_orders: { name: 'Bestellungen', type: 'number' },
                anteil: { name: 'Anteil', type: 'number', digits: 1, attach: ' %' },
                paid_orders: { name: 'Bezahlte Orders', type: 'number' },
                total_revenue: { name: 'Umsatz', type: 'number', digits: 2, attach: ' €' },
                expected_revenue: {
                  name: TERMS.anfoWert.label,
                  type: 'number',
                  digits: 2,
                  attach: ' €',
                },
                expected_anfo_value: {
                  name: `Ø ${TERMS.anfoWert.label}`,
                  type: 'number',
                  digits: 2,
                  attach: ' €',
                },
                conversion_rate: { name: 'Wandlungsrate', type: 'number', digits: 1, attach: ' %' },
                churn_rate: { name: 'Widerrufsrate', type: 'number', digits: 1, attach: ' %' },
                retention_rate: { name: 'Retention Rate', type: 'number', digits: 1, attach: ' %' },
                abo_2_60_days: { name: 'Abo2 (60d)', type: 'number', digits: 1, attach: ' %' },
                abo_4_120_days: { name: 'Abo4 (120d)', type: 'number', digits: 1, attach: ' %' },
                abo_4_120_days_quarterly: {
                  name: 'Abo4 Q (120d)',
                  type: 'number',
                  digits: 1,
                  attach: ' %',
                },
                abo_4_120_days_upsell: {
                  name: 'Abo4 U (120d)',
                  type: 'number',
                  digits: 1,
                  attach: ' %',
                },
                avg_lifetime_value: {
                  name: 'Ø Umsatz je zahlende Anfo',
                  type: 'number',
                  digits: 2,
                  attach: ' €',
                },
                avg_current_lifetime_days: {
                  name: 'Ø Aktuelle Lifetime',
                  type: 'number',
                  digits: 0,
                  attach: ' Tage',
                },
              }}
              initialSortColumn="total_revenue"
              sortOrder="desc"
              excelDownloadName="plx-wkz-performance"
            />
          </Box>
        </Box>
      )}

      {/* Bestellliste v2 (OrderList.tsx) */}
      <OrderList onOpenOrder={handleOpenOrder} onCount={setOrderCount} />

      {/* Legend */}
      <Box
        mt={4}
        p={3}
        bgcolor={colors.primary[400]}
        borderRadius="4px"
        boxShadow="0 2px 4px rgba(0,0,0,0.1)"
      >
        <Typography variant="h5" color={colors.greenAccent[500]} mb={2}>
          Legende
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" mb={1}>
              Vertragsstatus:
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label="V" color="error" size="small" />
                <Typography variant="body2">
                  <strong>Verweigert:</strong> Kunde hat bereits ein Abo oder wurde wegen Kündigung
                  in den letzten 90 Tagen blockiert
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label="L" color="success" size="small" />
                <Typography variant="body2">
                  <strong>Laufend:</strong> Aktives Abo (Testphase oder bezahlt)
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label="W" color="warning" size="small" />
                <Typography variant="body2">
                  <strong>Widerrufen:</strong> Gekündigt oder nicht bezahlt nach Testphase
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label="B" color="default" size="small" />
                <Typography variant="body2">
                  <strong>Beendet:</strong> Abo komplett abgelaufen
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" mb={1}>
              Abo-Typen:
            </Typography>
            <Box display="flex" flexDirection="column" gap={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label="Testphase" color="info" size="small" />
                <Typography variant="body2">
                  Neue Bestellung innerhalb der Testperiode (Standard: 30 Tage)
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label="Bezahlt" color="success" size="small" />
                <Typography variant="body2">Quartalsweise bezahltes Abo nach Testphase</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label="Upsell" color="secondary" size="small" />
                <Typography variant="body2">
                  Jahresabo mit Vorauszahlung - <strong>jährlich wiederkehrend</strong>
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip label="💶 Einmalfaktura" color="warning" size="small" />
                <Typography variant="body2">
                  <strong>Einmalige Zahlung</strong> - wird niemals automatisch verlängert oder
                  erneut abgebucht
                </Typography>
              </Box>
            </Box>

            <Typography variant="h6" mt={2} mb={1}>
              Preise und Abrechnungsmodelle:
            </Typography>
            <Typography variant="body2" mb={2}>
              📊 <strong>Quartalsweise:</strong> Reguläre Abonnements mit vierteljährlicher Zahlung
              (wiederkehrend)
              <br />📅 <strong>Jahresabos (Upsell):</strong> Vorauszahlung für ein Jahr, danach
              jährlich automatisch verlängert
              <br />💶 <strong>Einmalfaktura:</strong> Einmalige Rechnung ohne Abo-Verpflichtung -
              keine automatische Verlängerung
            </Typography>

            <Typography variant="h6" mt={2} mb={1}>
              Wichtige Unterschiede:
            </Typography>
            <Typography variant="body2" mb={2}>
              <strong>Upsell vs. Einmalfaktura:</strong>
              <br />• <strong>Upsell:</strong> Kunde zahlt jährlich im Voraus, Abo verlängert sich
              automatisch jedes Jahr
              <br />• <strong>Einmalfaktura:</strong> Kunde zahlt einmalig, keine automatische
              Verlängerung, kein wiederkehrendes Abo
            </Typography>

            <Typography variant="h6" mt={2} mb={1}>
              Performance-Metriken:
            </Typography>
            <Box display="flex" flexDirection="column" gap={1} mb={2}>
              <Typography variant="body2">
                <strong>Wandlungsrate:</strong> Von allen akzeptierten Orders (alle außer Status
                &quot;V&quot;), wie viele haben mindestens €1 bezahlt
              </Typography>
              <Typography variant="body2">
                <strong>AboCash (30d):</strong> Von Orders die ≥30 Tage alt sind, wie viele haben
                bezahlt (&gt;€1) UND mindestens 30 Tage überlebt (1 Monat = erste Bezahlperiode)
              </Typography>
              <Typography variant="body2">
                <strong>Abo2 (60d):</strong> Von Orders die ≥60 Tage alt sind, wie viele haben
                bezahlt UND mindestens 60 Tage überlebt (2 Quartale = Retention nach erstem Quartal)
              </Typography>
              <Typography variant="body2">
                <strong>Abo4 (120d):</strong> Von Orders die ≥120 Tage alt sind, wie viele haben
                bezahlt UND mindestens 120 Tage überlebt (4 Quartale = Langzeit-Retention)
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Quarterly (Q) / Annual (U):</strong> Retention-Raten getrennt nach Abo-Typ.
                Nenner ist jeweils nur die entsprechende Abo-Art (z.B. nur Quartalsabos für
                &quot;Abo4 Q&quot;). Zeigt Retention-Performance innerhalb jedes Abo-Typs.
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontStyle: 'italic',
                  mt: 1,
                  backgroundColor: colors.primary[500],
                  p: 1,
                  borderRadius: 1,
                }}
              >
                💡 <strong>Wichtig:</strong> AboCash/Abo2/Abo4 können höher sein als Wandlungsrate,
                da nur etablierte Orders (≥30/60/120 Tage) gezählt werden. Diese haben eine höhere
                Erfolgsrate durch Survivorship Bias - neuere Orders hatten noch keine Zeit zum
                Konvertieren.
              </Typography>
            </Box>

            <Typography variant="h6" mt={2} mb={1}>
              Hinweise:
            </Typography>
            <Typography variant="body2">
              • Banner-Parameter werden automatisch aus der URL extrahiert
              <br />• Bei Upsell-Bestellungen wird die ursprüngliche Bestellung nicht angezeigt
              <br />• Einmalfaktura-Produkte: 2010002 (RTP), 2010003 (TAM)
              <br />• Einmalfakturas haben keine Testphase (trial_days = 0)
            </Typography>
          </Grid>
        </Grid>
      </Box>

      {/* Order Details Modal */}
      <OrderDetailsModal
        open={modalOpen}
        onClose={handleModalClose}
        ordernumber={selectedOrderNumber}
      />
    </Box>
  );
};

export default PlxOrdersPage;
